import React from 'react';
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import App from './App.tsx'
import './index.css'
import { AuthProvider } from "@/hooks/useAuth";

/**
 * Platform Detection and Body Class Setup
 * Adds .platform-android, .platform-ios, or .platform-web class to body
 * This enables platform-specific CSS targeting
 */
function setupPlatformClass() {
  const userAgent = navigator.userAgent.toLowerCase();
  
  // Check for Capacitor native app first
  const isCapacitor = 'Capacitor' in window;
  
  // Detect platform
  let platform = 'web';
  if (isCapacitor) {
    // In Capacitor, check for Android or iOS
    if (userAgent.includes('android')) {
      platform = 'android';
    } else if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod')) {
      platform = 'ios';
    }
  } else {
    // Web browser detection
    if (userAgent.includes('android')) {
      platform = 'android';
    } else if (userAgent.includes('iphone') || userAgent.includes('ipad') || userAgent.includes('ipod')) {
      platform = 'ios';
    }
  }
  
  // Add platform class to body
  document.body.classList.add(`platform-${platform}`);
  
  // Also set as data attribute for CSS selectors
  document.documentElement.setAttribute('data-platform', platform);
  
  console.log(`[STRAIV] Platform detected: ${platform}`);
}

// Initialize platform detection immediately
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupPlatformClass);
  } else {
    setupPlatformClass();
  }
}

// Initialize Google Auth asynchronously to prevent app crash
(async () => {
  try {
    const { Capacitor } = await import('@capacitor/core');
    const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
    const platform = Capacitor.getPlatform();
    
    // Get client ID based on platform
    // IMPORTANT: For Android, use the WEB Client ID, not the Android Client ID
    // The Android Client ID is only for app identification (google-services.json)
    // The WEB Client ID is used for the actual sign-in flow
    let clientId: string;
    
    if (platform === 'ios') {
      clientId = '5370505998-758l7b9hr488v29cefv5t71uied9gvv7.apps.googleusercontent.com';
    } else if (platform === 'android') {
      // Android MUST use the WEB Client ID for sign-in (client_type: 3)
      clientId = '656278119230-qep1aon07kqn1f7prjpar3urq6c8bbdr.apps.googleusercontent.com';
    } else {
      clientId = '5370505998-hakrhril4cl729qhlhg5gq2c67c9hds9.apps.googleusercontent.com';
    }
    
    await GoogleAuth.initialize({
      clientId: clientId,
      scopes: ['profile', 'email'],
      grantOfflineAccess: true,
    });
    console.log('GoogleAuth initialized successfully with client:', platform, clientId);
  } catch (error) {
    console.error('GoogleAuth initialization error:', error);
  }
})();

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BrowserRouter>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <AuthProvider>
          <App />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>
);
